import { useEffect, useState } from 'react';

export interface CalendarEvent {
  id: string;
  title: string;
  type: string;
  startDate: string;
  endDate?: string | null;
  description?: string;
  program?: string;
}

export const EVENT_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'exam', label: 'Exams' },
  { value: 'holiday', label: 'Holidays' },
  { value: 'deadline', label: 'Deadlines' },
  { value: 'event', label: 'Events' },
  { value: 'info', label: 'Info' },
];

// Accent color per event type (dark theme palette).
export function eventAccent(type: string): string {
  switch (type) {
    case 'exam': return '#EF4444';
    case 'holiday': return '#10B981';
    case 'deadline': return '#F59E0B';
    case 'event': return '#14B8A6';
    case 'info': return '#60A5FA';
    default: return 'rgba(255,255,255,.2)';
  }
}

export function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  try {
    // Clean up the string
    let cleaned = dateStr
      .replace(/\(.*?\)/g, '') // Remove parentheses content like (Monday)
      .replace(/\s+/g, ' ')
      .trim();

    // Fix common typos
    cleaned = cleaned.replace(/Januray/gi, 'January');

    // Handle weird format like "January 19, January 23, 2026" - take first date with year
    const parts = cleaned.split(/,/).map(p => p.trim());
    if (parts.length > 1) {
      const yearMatch = cleaned.match(/20\d{2}/);
      const year = yearMatch ? yearMatch[0] : '';

      const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
        'july', 'august', 'september', 'october', 'november', 'december'];

      for (const part of parts) {
        const monthMatch = part.toLowerCase().match(new RegExp(monthNames.join('|')));
        const dayMatch = part.match(/\b(\d{1,2})\b/);

        if (monthMatch && dayMatch) {
          const monthIndex = monthNames.indexOf(monthMatch[0]);
          const day = parseInt(dayMatch[1]);
          const yearNum = year ? parseInt(year) : new Date().getFullYear();
          return new Date(yearNum, monthIndex, day);
        }
      }
    }

    // Handle range dates - extract first date
    if (cleaned.includes(' to ') || cleaned.includes(' - ') || cleaned.includes(' -- ')) {
      cleaned = cleaned.split(/\s+to\s+|\s+-+\s+/i)[0].trim();
    }

    // Handle formats like "May, 25" or "23,March 2026"
    cleaned = cleaned.replace(/,\s*/g, ' ').replace(/\s+/g, ' ').trim();

    // Handle format like "6-Jul-26"
    if (/^\d{1,2}-[A-Za-z]{3}-\d{2}$/.test(cleaned)) {
      const dateParts = cleaned.split('-');
      const day = dateParts[0];
      const month = dateParts[1];
      const year = '20' + dateParts[2];
      cleaned = `${month} ${day}, ${year}`;
    }

    // Try parsing
    const parsed = new Date(cleaned);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    // Try extracting month, day, year manually
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'];
    const monthMatch = cleaned.toLowerCase().match(new RegExp(monthNames.join('|')));
    const yearMatch = cleaned.match(/20\d{2}/);
    const dayMatch = cleaned.match(/\b(\d{1,2})\b/);

    if (monthMatch && yearMatch && dayMatch) {
      const monthIndex = monthNames.indexOf(monthMatch[0]);
      const year = parseInt(yearMatch[0]);
      const day = parseInt(dayMatch[1]);
      return new Date(year, monthIndex, day);
    }

    return null;
  } catch {
    return null;
  }
}

// parseDate() returns the START of a range. For "has this finished?" questions we
// need the END, e.g. "June 08, 2026 -June 12, 2026 2026" -> June 12 2026.
export function parseDateRangeEnd(dateStr: string): Date | null {
  if (!dateStr) return null;

  let cleaned = dateStr.replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim();

  // "6-Jul-26" is a single date, not a range — don't split it on the dashes.
  if (/^\d{1,2}-[A-Za-z]{3}-\d{2}$/.test(cleaned)) return parseDate(dateStr);

  const segments = cleaned
    .split(/\s+to\s+|\s*-{1,2}\s*(?=[A-Za-z0-9])/i)
    .map(s => s.trim())
    .filter(Boolean);

  // Walk from the last segment back; the tail may be junk like a stray "2026".
  for (let i = segments.length - 1; i >= 0; i--) {
    const parsed = parseDate(segments[i]);
    if (parsed) return parsed;
  }
  return parseDate(dateStr);
}

export function formatDate(dateStr: string): string {
  const parsed = parseDate(dateStr);
  if (parsed) {
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return dateStr.replace(/\(.*?\)/g, '').trim() || dateStr;
}

export function getDaysUntil(dateStr: string): string {
  const eventDate = parseDate(dateStr);
  if (!eventDate) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eventDate.setHours(0, 0, 0, 0);

  const diffTime = eventDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays > 7 && diffDays <= 30) return `In ${Math.ceil(diffDays / 7)} weeks`;
  return '';
}

// Split filtered events into upcoming/past, preserving original order; events
// without a parseable date inherit the status of the previous dated event.
export function categorizeEvents(filtered: CalendarEvent[]) {
  const upcoming: CalendarEvent[] = [];
  const past: CalendarEvent[] = [];
  let lastEventWasPast = false;

  for (const event of filtered) {
    const eventDate = parseDate(event.startDate);
    if (eventDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const compareDate = new Date(eventDate.getTime());
      compareDate.setHours(23, 59, 59, 999);

      const isPast = compareDate < today;
      lastEventWasPast = isPast;
      (isPast ? past : upcoming).push(event);
    } else {
      (lastEventWasPast ? past : upcoming).push(event);
    }
  }
  return { upcoming, past };
}

export function exportToICS(events: CalendarEvent[], selectedType: string) {
  let icsContent = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//University//Academic Calendar//EN\n';
  const allFiltered = events.filter(event => selectedType === 'all' || event.type === selectedType);

  allFiltered.forEach(event => {
    const startDate = event.startDate.replace(/[^0-9]/g, '');
    icsContent += 'BEGIN:VEVENT\n';
    icsContent += `SUMMARY:${event.title}\n`;
    icsContent += `DESCRIPTION:${event.description}\n`;
    icsContent += `DTSTART:${startDate}\n`;
    if (event.endDate) {
      const endDate = event.endDate.replace(/[^0-9]/g, '');
      icsContent += `DTEND:${endDate}\n`;
    }
    icsContent += 'END:VEVENT\n';
  });

  icsContent += 'END:VCALENDAR';

  const blob = new Blob([icsContent], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'academic-calendar.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function useAcademicEvents() {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await fetch('/academic_calendar.json');
        const data = await response.json();

        const allEvents: CalendarEvent[] = [];
        const calendar = data.academic_calendar;

        if (calendar) {
          Object.entries(calendar).forEach(([semesterKey, semesterData]: [string, any]) => {
            const semesterName = semesterKey.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

            if (semesterData.events) {
              semesterData.events.forEach((event: any, index: number) => {
                const startDate = event.start_date || event.date || '';
                allEvents.push({
                  id: `${semesterKey}-${index}`,
                  title: event.event,
                  type: event.details ? 'info' : (event.event.toLowerCase().includes('exam') ? 'exam' :
                    event.event.toLowerCase().includes('holiday') || event.event.toLowerCase().includes('break') ? 'holiday' : 'event'),
                  startDate,
                  endDate: event.end_date || null,
                  description: event.details || '',
                  program: semesterName,
                });
              });
            }
          });
        }

        setEvents(allEvents);
      } catch (error) {
        console.error('Failed to load calendar events:', error);
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  return { events, isLoading };
}
